import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { HttpServiceHelper } from '@reg/shared/service';
import { IdentityRankingPoints, PagedResponse } from '@reg/shared/model';
import { IJsonApiResponse } from '@reg/shared/json-api/json-api.interface';

/**
 * Service to get Sports
 */
@Injectable()
export class IdentityRankingPointsService {
    public static INCLUDE_EVENTS: string = 'events';
    private readonly identityApiBaseUrl: string;

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
        this.identityApiBaseUrl = http.configService.getBaseConfig('identityApiBaseUrl');
    }

    /**
     * Get a paged list of IdentityRankingPoints
     */
    public getIndex(seasonId: string, includes: string[] = null): Observable<PagedResponse> {
        const apiUrl: string = this.getApiUrl(null, includes, [`seasonId=${seasonId}`]);
        return this.getIndexPagedResponseByUrl(apiUrl);
    }

    /**
     * Get records by a url. This is used for paging since the response provides the proper page urls
     */
    public getIndexPagedResponseByUrl(apiUrl: string): Observable<PagedResponse> {
        return this.http.get(apiUrl).pipe(map((response: IJsonApiResponse) => {
            const json: any = response;
            const rankingPoints: IdentityRankingPoints[] =
                json.data.map((d: any) => new IdentityRankingPoints().deserialize(d, json.included));

            return new PagedResponse(
                json.links.first,
                json.links.last,
                json.links.prev,
                json.links.next,
                json.meta.total,
                rankingPoints,
            );
        }));
    }

    /**
     * Get a single ranking points record
     */
    public get(rankingPointsId: string, include: string[] = null): Observable<IdentityRankingPoints> {
        const apiRequestUrl: string = this.getApiUrl(rankingPointsId, include);

        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) =>
                new IdentityRankingPoints().deserialize(response, response.included)));
    }

    /**
     * Add or update a ranking points record
     */
    public save(
        rankingPoints: IdentityRankingPoints,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<IdentityRankingPoints> {
        let response: Observable<IJsonApiResponse>;
        const apiUrl: string = this.getApiUrl(rankingPoints.id, includes, queryParams);

        if (rankingPoints.id) {
            // Update existing ranking  points
            response = this.http.patch(apiUrl, rankingPoints.serialize());
        } else {
            // Add a new ranking  points
            response = this.http.post(apiUrl, rankingPoints.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityRankingPoints())
                    .deserialize(response, response.included)),
            );
    }

    /**
     * Delete the specified ranking points.
     */
    public delete(rankingPointsId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(rankingPointsId);
        return this.http.delete(apiRequestUrl).pipe(map(() => true));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        rankingPointsId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): string {
        let url: string = `${this.identityApiBaseUrl}/ranking-points/`;

        if (rankingPointsId) {
            url += rankingPointsId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}
